// useChangeStream — listen to the global "changes" topic the backend
// publishes after every successful mutation, and call `onChange` so the
// caller can re-fetch its data without polling.
//
//   useChangeStream((evt) => { fetchAssets(); }, ['assets']);
//
// `tables` filters the events the callback runs for. Pass an empty array
// or omit it to fire on every change.
//
// Mirrors useScanStream's auto-reconnect behaviour.
//
// PERF: callbacks are debounced (300ms trailing) so a burst of mutations
// (e.g. a continuous-mode scan firing 30 raw-inventory writes/sec) collapses
// into ONE refetch instead of N. Without this the page would slam its own
// fetch endpoint with one request per broadcast and feel laggy under load.

import { useEffect, useRef } from 'react';

const CHANGE_DEBOUNCE_MS = 300;

const buildWsUrl = (token) => {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  let host = window.location.host;
  let base = process.env.PUBLIC_URL || '';
  // CRA dev server (3000) doesn't proxy WS — hit backend (5001) directly (no nginx prefix).
  if (host.endsWith(':3000')) { host = host.replace(':3000', ':5001'); base = ''; }
  return `${proto}://${host}${base}/ws?token=${encodeURIComponent(token)}`;
};

export function useChangeStream(onChange, tables) {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closingRef = useRef(false);
  const cbRef = useRef(onChange);
  const tablesRef = useRef(tables);
  const pendingTimerRef = useRef(null);
  const pendingPayloadRef = useRef(null);

  // Keep refs up-to-date so a render that recreates the callback doesn't
  // tear down + reopen the socket.
  useEffect(() => { cbRef.current = onChange; }, [onChange]);
  useEffect(() => { tablesRef.current = tables; }, [tables]);

  useEffect(() => {
    closingRef.current = false;

    const flush = () => {
      pendingTimerRef.current = null;
      const payload = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      cbRef.current?.(payload);
    };

    const schedule = (payload) => {
      // Trailing-edge debounce: every new event resets the timer + remembers
      // the latest payload. The callback fires once per quiet window.
      pendingPayloadRef.current = payload;
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = setTimeout(flush, CHANGE_DEBOUNCE_MS);
    };

    const open = () => {
      const token = localStorage.getItem('identium_token');
      if (!token) return;
      const ws = new WebSocket(buildWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        ws.send(JSON.stringify({ type: 'subscribe', topic: 'changes' }));
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'event' && msg.topic === 'changes') {
            const filter = tablesRef.current;
            if (filter && filter.length && !filter.includes(msg.payload?.table)) return;
            schedule(msg.payload);
          }
        } catch {}
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        wsRef.current = null;
        if (closingRef.current) return;
        const delay = Math.min(15000, 500 * Math.pow(2, retryRef.current));
        retryRef.current += 1;
        setTimeout(open, delay);
      };
    };

    open();
    return () => {
      closingRef.current = true;
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
    };
  }, []); // open exactly once for the page lifetime
}
